import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { toast } from 'sonner';
import api, { errMsg } from '../../../lib/api';

export const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
export const SEL = 'w-full h-9 rounded-xl border border-[#E5E0EA] bg-white px-3 text-sm';
export const Lbl = ({ t, children }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-wider text-[#6E5F78] font-semibold">{t}</span><div className="mt-1">{children}</div></label>
);

// Add or edit a repeating weekly session. Pick several weekdays to repeat the same block.
export const SessionDialog = ({ open, initial, meta, clients, staff, onClose, onSaved }) => {
  const editing = !!initial?.session_id;
  const [f, setF] = useState(null);
  const form = f || { client_id: '', weekdays: [], start: '08:00', end: '12:00', type: 'direct', staff_id: '', effective_from: '', effective_to: '', location: 'Center', ...initial, ...(initial?.weekday ? { weekdays: [initial.weekday] } : {}) };
  const set = (k, v) => setF({ ...form, [k]: v });
  const toggleDay = (d) => set('weekdays', editing ? [d] : form.weekdays.includes(d) ? form.weekdays.filter((x) => x !== d) : [...form.weekdays, d]);
  const pool = staff.filter((s) => s.active !== false && (form.type === 'supervision' ? ['BCBA', 'BCaBA'].includes(s.role) : true));

  const save = async () => {
    const body = { ...form, staff_id: form.staff_id || null };
    try {
      if (editing) await api.put(`/ludo/schedule/sessions/${initial.session_id}`, body);
      else await api.post('/ludo/schedule/sessions', body);
      toast.success(editing ? 'Session updated' : 'Session added'); setF(null); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setF(null); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="sched-session-dialog">
        <DialogHeader><DialogTitle className="font-serif">{editing ? 'Edit repeating session' : 'Add repeating session'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Lbl t="Client">
            <select className={SEL} value={form.client_id} onChange={(e) => set('client_id', e.target.value)} data-testid="sched-session-client">
              <option value="">Pick a client…</option>
              {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name} · {c.stage}</option>)}
            </select></Lbl></div>
          <div className="col-span-2"><Lbl t={editing ? 'Day' : 'Days (repeats every week)'}>
            <div className="flex gap-1 flex-wrap">{meta.days.map((d) => (
              <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`sched-session-day-${d}`}
                className={`px-3 py-1.5 rounded-full text-[12px] border ${form.weekdays.includes(d) ? 'bg-[#5E5068] text-white border-[#5E5068]' : 'bg-white border-[#E5E0EA] text-[#5E5068]'}`}>{DAY_LABEL[d]}</button>
            ))}</div></Lbl></div>
          <Lbl t="Start"><Input type="time" value={form.start} onChange={(e) => set('start', e.target.value)} className="rounded-xl h-9" data-testid="sched-session-start" /></Lbl>
          <Lbl t="End"><Input type="time" value={form.end} onChange={(e) => set('end', e.target.value)} className="rounded-xl h-9" data-testid="sched-session-end" /></Lbl>
          <Lbl t="Service">
            <select className={SEL} value={form.type} onChange={(e) => set('type', e.target.value)} data-testid="sched-session-type">
              {Object.entries(meta.session_types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></Lbl>
          <Lbl t="Staff">
            <select className={SEL} value={form.staff_id || ''} onChange={(e) => set('staff_id', e.target.value)} data-testid="sched-session-staff">
              <option value="">Unassigned (shows as a gap)</option>
              {pool.map((s) => <option key={s.staff_id} value={s.staff_id}>{s.name} · {s.role}</option>)}
            </select></Lbl>
          <Lbl t="Starts on (optional)"><Input type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} className="rounded-xl h-9" data-testid="sched-session-from" /></Lbl>
          <Lbl t="Ends on (optional)"><Input type="date" value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} className="rounded-xl h-9" data-testid="sched-session-to" /></Lbl>
          <Lbl t="Location">
            <select className={SEL} value={form.location} onChange={(e) => set('location', e.target.value)} data-testid="sched-session-location">
              {['Center', 'Home', 'School', 'Community', 'Telehealth'].map((x) => <option key={x}>{x}</option>)}
            </select></Lbl>
        </div>
        <p className="text-[11px] text-[#8A7B95]">Tip: split a long day between two BTs by adding a morning block for one BT and an afternoon block for another.</p>
        <Button onClick={save} disabled={!form.client_id || !form.weekdays.length} className="rounded-xl bg-[#5E5068] text-white" data-testid="sched-session-save">
          {editing ? 'Save session' : 'Add session'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default SessionDialog;
